import { type ReactNode } from 'react';
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Text,
  makeStyles,
  tokens,
} from '@coreai/fluentui-react';
import { customScrollbarStyles } from '@/styles/scrollbar';
import { TableSkeleton } from '@/components/shared/TableSkeleton';

const useStyles = makeStyles({
  root: {
    flex: 1,
    minHeight: 0,
    overflow: 'auto',
    ...customScrollbarStyles,
  },
  table: {
    width: '100%',
    // The Connections page lists are read-only — rows are not clickable.
    // Fluent v9 TableRow has a hover background by default which reads as a
    // false affordance ("am I supposed to click this?"). Neutralize it so
    // hover stays in line with the row's surface color.
    '& tbody tr:hover': {
      backgroundColor: 'transparent',
    },
  },
  status: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacingHorizontalS,
    padding: tokens.spacingVerticalXXL,
    color: tokens.colorNeutralForeground2,
  },
  emptyIcon: {
    flexShrink: 0,
    color: tokens.colorNeutralForeground3,
    '@media (forced-colors: active)': {
      color: 'CanvasText',
    },
  },
  emptyText: {
    color: tokens.colorNeutralForeground2,
  },
});

export interface ConnectionTypeColumn<T> {
  /** Column header text */
  header: string;
  /** Cell renderer; receives the entry key (id) and value */
  render: (id: string, value: T) => ReactNode;
  /** Optional className applied to TableCell */
  cellClassName?: string;
  /** Optional className applied to TableHeaderCell */
  headerClassName?: string;
}

export interface ConnectionTypeSectionProps<T> {
  /** Entries to render — typically `Object.entries(record)` */
  entries: Array<[string, T]>;
  /** Column descriptors */
  columns: Array<ConnectionTypeColumn<T>>;
  /** True while data is loading */
  isLoading: boolean;
  /** True if loading errored */
  isError: boolean;
  /** Retry handler shown next to the error message */
  onRetry?: () => void;
  /** Icon shown in the empty state (an explicit React node so callers control sizing) */
  emptyIcon: ReactNode;
  /** Empty-state copy (single short sentence) */
  emptyMessage: string;
  /** Loading-state spinner label */
  loadingLabel?: string;
  /** Error-state heading text */
  errorMessage?: string;
  /** aria-label for the underlying <Table> */
  ariaLabel: string;
  /** Optional row-key extractor; defaults to the entry id */
  getRowKey?: (id: string, value: T) => string;
}

/**
 * Generic, data-driven section for a single connection-type tab.
 *
 * One source of truth for the loading / error / empty / table chrome shared
 * across Managed, Service Provider, Agent, and MCP tabs. Status regions are
 * announced politely so screen readers hear state transitions when switching
 * tabs or refetching.
 */
export const ConnectionTypeSection = <T,>({
  entries,
  columns,
  isLoading,
  isError,
  onRetry,
  emptyIcon,
  emptyMessage,
  loadingLabel = 'Loading connections...',
  errorMessage = 'Failed to load connections.',
  ariaLabel,
  getRowKey,
}: ConnectionTypeSectionProps<T>) => {
  const styles = useStyles();

  if (isLoading) {
    return <TableSkeleton rows={5} columns={Math.max(columns.length, 1)} label={loadingLabel} />;
  }

  if (isError) {
    return (
      <div className={styles.status} role="alert">
        <Text>
          {errorMessage}{' '}
          {onRetry && (
            <Button appearance="transparent" onClick={onRetry}>
              Retry
            </Button>
          )}
        </Text>
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <div className={styles.status} role="status" aria-live="polite">
        <span className={styles.emptyIcon} aria-hidden="true">
          {emptyIcon}
        </span>
        <Text className={styles.emptyText}>{emptyMessage}</Text>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <Table className={styles.table} size="medium" aria-label={ariaLabel}>
        <TableHeader>
          <TableRow>
            {columns.map((col, i) => (
              <TableHeaderCell key={`${col.header}-${i}`} className={col.headerClassName}>
                {col.header}
              </TableHeaderCell>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {entries.map(([id, value]) => (
            <TableRow key={getRowKey ? getRowKey(id, value) : id}>
              {columns.map((col, i) => (
                <TableCell key={`${id}-${i}`} className={col.cellClassName}>
                  {col.render(id, value)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

ConnectionTypeSection.displayName = 'ConnectionTypeSection';
